import { type AuthenticatedUser, scopeFor } from '@testflow/shared';

/**
 * Woraufhin sind die Zahlen dieser Seite beschnitten? `null` = projektweit (kein Hinweis).
 *
 * Seit dem Knoten-Filter in Reporting und Sign-off zeigt dieselbe Kachel je nach Nutzer eine
 * ANDERE Zahl bei gleicher Beschriftung. Ohne diesen Hinweis liest ein Verantwortlicher für
 * „Vertrieb" den Fortschritt seines Teilprojekts als Projektstand.
 *
 * Bewusst aus den Rollen des Nutzers abgeleitet und nicht aus der Antwort des Servers: es ist
 * wörtlich dieselbe Funktion (`scopeFor` aus @testflow/shared), die auch serverseitig
 * entscheidet, und sie gilt für alle betroffenen Seiten gleich.
 *
 * Der Nutzer MUSS vorher auf das Projekt gescopt sein (`useScopedUser()`) — sonst urteilt die
 * Zeile projektübergreifend, genau der Fehler aus #23.
 *
 * Review-Fund (Whole-Branch-Review, Fund 1): `label` ist `null`, wenn ein Zuschnitt zwar aktiv
 * ist, die Knotennamen aber (noch) nicht auflösbar sind — z. B. weil die Namen-Abfrage einer
 * Seite noch lädt, fehlgeschlagen ist oder langsamer war als die (längst gefilterte) Kennzahlen-
 * Abfrage. VORHER lieferte diese Funktion in genau diesem Fall `null` insgesamt zurück — die Seite
 * zeigte dann beschnittene Zahlen OHNE jeden Hinweis, exakt der Zustand, den der Hinweis verhindern
 * soll. Ein aufrufender Screen MUSS darum beide Fälle von `label` bedienen: `string` → Zuschnitt
 * mit Knotennamen, `null` → Zuschnitt aktiv, aber (noch) ohne Namen — ein namenloser Rückfalltext
 * statt gar keinem Hinweis. Nur der äußere `null`-Rückgabewert (kein Objekt) bedeutet "kein
 * Zuschnitt aktiv, kein Hinweis nötig".
 */
export function scopeHintFrom(
  user: Pick<AuthenticatedUser, 'roles'> | null,
  nodes: { node_id: string; name: string }[],
): { node_ids: string[]; label: string | null } | null {
  if (!user) return null;
  const scope = scopeFor(user as AuthenticatedUser, 'report.read');
  if (scope === null || scope.kind === 'projectwide') return null;
  const byId = new Map(nodes.map((n) => [n.node_id, n.name]));
  const names = scope.node_ids.map((id) => byId.get(id));
  // Solange die Knotenliste noch lädt (oder ein Knoten gelöscht wurde) wäre eine rohe UUID im
  // Hinweistext schlimmer als kein Hinweis — aber der Zuschnitt selbst bleibt aktiv und MUSS
  // gemeldet werden (Fund 1): `label: null` statt der Funktion insgesamt `null` zurückzugeben.
  if (names.some((n) => n === undefined)) {
    return { node_ids: scope.node_ids, label: null };
  }
  return { node_ids: scope.node_ids, label: (names as string[]).join(', ') };
}
